/**
 * Utility functions for table calculations
 */

/**
 * Calculate maximum height based on window dimensions and instruction state
 * @param {boolean} isInstructionOpen - Whether instruction panel is open
 * @returns {number} - Calculated max height
 */
export function calculateMaxHeight(isInstructionOpen) {
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  let offset;

  if ((windowWidth <= 980 && windowWidth > 768) || (windowWidth < 550)) {
    offset = isInstructionOpen ? 550 : 500;
  } else if ((windowWidth <= 1000 && windowWidth > 980) || (windowWidth < 654)) {
    offset = isInstructionOpen ? 585 : 450;
  } else {
    offset = isInstructionOpen ? 525 : 385;
  }

  return windowHeight - offset;
}

/**
 * Calculate column widths based on content
 * @param {Array} columns - Table columns
 * @param {Array} rows - Table rows
 * @param {number} getLongestString - Longest string length
 * @param {boolean} splitMode - Whether split mode is enabled
 * @param {Object} attributeSeparationLines - Separation line configurations
 * @returns {Array} - Array of column widths
 */
export function calculateColumnWidths(columns, rows, getLongestString, splitMode, attributeSeparationLines) {
  const widths = [];
  
  widths.push(getLongestString * 10);
  
  for (let colIndex = 1; colIndex < columns.length; colIndex++) {
    const columnHeader = columns[colIndex];
    let maxLength = columnHeader ? columnHeader.length : 0;
    
    rows.forEach(row => {
      if (row[colIndex]) {
        const cellContent = Array.isArray(row[colIndex]) ? row[colIndex][0] : row[colIndex];
        const cellLength = cellContent ? cellContent.toString().length : 0;
        maxLength = Math.max(maxLength, cellLength);
      }
    });
    
    let maxSubMenuLength = 0;
    if (splitMode && attributeSeparationLines) {
      Object.values(attributeSeparationLines).forEach(separationLine => {
        if (Array.isArray(separationLine) && separationLine.length >= 3) {
          const [, specialistsLabel, faoLabel] = separationLine;
          
          if (specialistsLabel && faoLabel) {
            const specialistsLength = specialistsLabel.length || 0;
            const faoLength = faoLabel.length || 0;
            
            const pixelsPerChar = 10; 
            const paddingTotal = 16; 
            const borderSpace = 2;
            const percentSymbol = 12; 
            const plPadding = 8;

            const faoWidth = faoLength * pixelsPerChar + plPadding;
            const specialistsWidth = specialistsLength * pixelsPerChar;
            
            const totalSubMenuLength = Math.max(
              specialistsWidth + paddingTotal + borderSpace + percentSymbol,
              (faoWidth * 2) + paddingTotal + borderSpace + percentSymbol
            );
            
            const charEquivalent = totalSubMenuLength / 14; 
            
            maxSubMenuLength = Math.max(maxSubMenuLength, charEquivalent);
          }
        }
      });
    }
    
    const finalMaxLength = Math.max(maxLength, maxSubMenuLength);
    const calculatedWidth = Math.max(250, Math.min(800, (finalMaxLength + 7) * 14));
    widths.push(calculatedWidth);
  }
  
  return widths;
}

/**
 * Calculate subdivision width for split mode columns
 * @param {number} colIndex - Column index
 * @param {string} type - Type ('fao' or 'specialists')
 * @param {Object} subdivisionWidths - Current subdivision widths
 * @param {Object} attributeSeparationLines - Separation line configurations
 * @returns {string} - Width value (percentage or pixels)
 */
export function getSubdivisionWidth(colIndex, type, subdivisionWidths, attributeSeparationLines) {
  const hasGlobalSubdivisions = attributeSeparationLines && 
    Object.values(attributeSeparationLines).some(separationLine => {
      if (Array.isArray(separationLine) && separationLine.length >= 3) {
        const [, specialistsLabel] = separationLine;
        return specialistsLabel && specialistsLabel !== 'Especialistas' && specialistsLabel.trim() !== '';
      }
      return false;
    });
  
  if (!hasGlobalSubdivisions) {
    return type === 'fao' ? '40%' : '60%';
  }
  
  const capturedWidth = subdivisionWidths[colIndex]?.[type];
  if (capturedWidth && capturedWidth > 0) {
    return capturedWidth + 'px';
  }
  
  return '50%';
}

/**
 * Get longest string length from rows
 * @param {Array} rows - Table rows
 * @param {number} maxLength - Maximum allowed length
 * @returns {number} - Longest string length
 */
export function getLongestStringLength(rows, maxLength = 60) {
  return rows.reduce((longest, row) => {
    const currentLength = row[0].length;
    return currentLength > longest && currentLength <= maxLength 
      ? currentLength 
      : longest;
  }, 0) + 7;
}
